import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bell, ImagePlus, Loader2 } from "lucide-react";
import { api, apiError, BACKEND } from "../../lib/api";
import { useLang } from "../../lib/i18n";

export const EMPTY_BANNER = { name: "", image_url: "", fit: "contain", bg: "#000000", link: "", starts_at: "", ends_at: "", active: true };
const SWATCHES = [["black", "#000000"], ["white", "#FFFFFF"], ["navy", "#0D2A6B"]];
export const bannerSrc = (url) => (url?.startsWith("/api/") ? `${BACKEND}${url}` : url);

// Average color of the image border, sampled on a small canvas.
async function edgeColor(url) {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = url;
  await img.decode();
  const s = 48;
  const c = Object.assign(document.createElement("canvas"), { width: s, height: s });
  const ctx = c.getContext("2d");
  ctx.drawImage(img, 0, 0, s, s);
  const d = ctx.getImageData(0, 0, s, s).data;
  let r = 0, g = 0, b = 0, n = 0;
  for (let y = 0; y < s; y++) for (let x = 0; x < s; x++) {
    if (x > 1 && x < s - 2 && y > 1 && y < s - 2) continue;
    const i = (y * s + x) * 4;
    r += d[i]; g += d[i + 1]; b += d[i + 2]; n++;
  }
  return "#" + [r, g, b].map((v) => Math.round(v / n).toString(16).padStart(2, "0")).join("");
}

export function BannerPreview({ b }) {
  return (
    <div className="relative mx-auto aspect-[390/338] w-full max-w-[300px] overflow-hidden rounded-2xl border border-slate-200 shadow-inner"
      style={{ background: b.fit === "contain" ? b.bg : "#E8EEF7" }} data-testid="ads-preview">
      {b.image_url ? (
        <img src={bannerSrc(b.image_url)} alt="" className={`h-full w-full ${b.fit === "contain" ? "object-contain" : "object-cover"}`} />
      ) : <div className="flex h-full items-center justify-center text-xs text-slate-400">390 × 338</div>}
      <span className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/75 backdrop-blur"><Bell className="h-3.5 w-3.5 text-[#0D47A1]" /></span>
      <span className="absolute right-2 top-2 rounded-full bg-white/75 px-2 py-0.5 text-[10px] font-bold text-navy backdrop-blur">ES | EN</span>
    </div>
  );
}

const Label = ({ children }) => <p className="mb-1.5 text-sm font-bold text-navy">{children}</p>;

export function BannerForm({ initial, onSaved, onCancel }) {
  const { t } = useLang();
  const [b, setB] = useState({ ...EMPTY_BANNER, ...initial, link: initial?.link || "", starts_at: initial?.starts_at || "", ends_at: initial?.ends_at || "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [auto, setAuto] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (v) => setB((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!auto || !b.image_url) return;
    edgeColor(bannerSrc(b.image_url)).then(set("bg")).catch(() => toast.error(t("ads.autoFail")));
  }, [auto, b.image_url]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error(t("ads.tooBig"));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      set("image_url")(`/api/files/${data.path}`);
    } catch (e) {
      toast.error(apiError(e, t));
    }
    setUploading(false);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!b.name.trim() || !b.image_url) return toast.error(t("ads.required"));
    setBusy(true);
    const payload = { ...b, name: b.name.trim(), link: b.link || null, starts_at: b.starts_at || null, ends_at: b.ends_at || null };
    try {
      if (initial?.id) await api.put(`/admin/banners/${initial.id}`, payload);
      else await api.post("/admin/banners", payload);
      toast.success(t("ads.saved"));
      onSaved();
    } catch (err) {
      toast.error(apiError(err, t));
    }
    setBusy(false);
  };

  return (
    <form onSubmit={save} className="space-y-5 rounded-3xl bg-white p-5 shadow-[0_4px_20px_rgba(13,42,107,0.06)]" data-testid="ads-form">
      <BannerPreview b={b} />
      <div>
        <Label>{t("ads.name")}</Label>
        <input className="field" value={b.name} onChange={(e) => set("name")(e.target.value)} maxLength={80} placeholder="Niki's American Diner" data-testid="ads-name-input" />
      </div>
      <div>
        <Label>{t("ads.image")}</Label>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="ads-file-input" />
        <button type="button" onClick={() => fileRef.current?.click()} className="btn-outline w-full py-3" disabled={uploading} data-testid="ads-upload-btn">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} {t(b.image_url ? "ads.replaceImage" : "ads.uploadImage")}
        </button>
        <p className="mt-1 text-xs text-slate-500">{t("ads.imageHint")}</p>
      </div>
      <div>
        <Label>{t("ads.mode")}</Label>
        <div className="grid grid-cols-2 gap-2">
          {["contain", "cover"].map((m) => (
            <button key={m} type="button" onClick={() => set("fit")(m)} data-testid={`ads-fit-${m}`}
              className={`rounded-xl border-2 px-2 py-2.5 text-xs font-bold transition-colors ${b.fit === m ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-navy"}`}>
              {t(`ads.fit.${m}`)}
            </button>
          ))}
        </div>
      </div>
      {b.fit === "contain" && (
        <div>
          <Label>{t("ads.bg")}</Label>
          <div className="flex flex-wrap items-center gap-2">
            {SWATCHES.map(([k, hex]) => (
              <button key={k} type="button" onClick={() => { setAuto(false); set("bg")(hex); }} data-testid={`ads-bg-${k}`}
                className={`h-9 rounded-full border-2 px-3 text-xs font-bold ${!auto && b.bg.toUpperCase() === hex ? "border-brand" : "border-slate-200"}`}
                style={{ background: hex, color: k === "white" ? "#0D2A6B" : "#fff" }}>{t(`ads.bg.${k}`)}</button>
            ))}
            <button type="button" onClick={() => setAuto(true)} disabled={!b.image_url} data-testid="ads-bg-auto"
              className={`h-9 rounded-full border-2 px-3 text-xs font-bold text-navy disabled:opacity-40 ${auto ? "border-brand" : "border-slate-200"}`}>
              {t("ads.bg.auto")}{auto && <span className="ml-1.5 inline-block h-3 w-3 rounded-full align-[-2px] ring-1 ring-slate-300" style={{ background: b.bg }} />}
            </button>
          </div>
        </div>
      )}
      <div>
        <Label>{t("ads.link")}</Label>
        <input className="field" value={b.link} onChange={(e) => set("link")(e.target.value)} placeholder="https://…" inputMode="url" data-testid="ads-link-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>{t("ads.start")}</Label><input type="date" className="field" value={b.starts_at} onChange={(e) => set("starts_at")(e.target.value)} data-testid="ads-start-input" /></div>
        <div><Label>{t("ads.end")}</Label><input type="date" className="field" value={b.ends_at} onChange={(e) => set("ends_at")(e.target.value)} data-testid="ads-end-input" /></div>
      </div>
      <label className="flex items-center justify-between rounded-xl bg-mist px-4 py-3 text-sm font-bold text-navy">
        {t("ads.active")}
        <input type="checkbox" checked={b.active} onChange={(e) => set("active")(e.target.checked)} className="h-5 w-5 accent-[#1E88E5]" data-testid="ads-active-checkbox" />
      </label>
      <div className="flex gap-2.5">
        <button type="button" onClick={onCancel} className="btn-outline flex-1 py-3" data-testid="ads-cancel-btn">{t("ads.cancel")}</button>
        <button type="submit" disabled={busy || uploading} className="btn-primary flex-1 py-3" data-testid="ads-save-btn">{t("ads.save")}</button>
      </div>
    </form>
  );
}
